/**
 * @fileoverview Interface para respuestas del endpoint de login con control de bloqueos
 * @module interfaces/loginResponse
 */

/**
 * @typedef {Object} LoginResponse
 * @description Respuesta estándar del endpoint de login (POST /api/v2/usuarios/ingresos)
 * 
 * @property {string} codigoRespuesta - Código de respuesta: "01" = éxito, "99" = error
 * @property {string} mensajeRespuesta - Mensaje descriptivo de la respuesta
 * 
 * @property {boolean} bloqueado - Indica si la cuenta está bloqueada
 * @property {('TEMPORAL'|'PERMANENTE'|'BLOQUEO_INTENTOS'|null)} tipoBloqueo - Tipo de bloqueo aplicado
 * @property {(number|null)} intentosRestantes - Intentos restantes antes del bloqueo (2, 1, 0)
 * @property {(number|null)} minutosParaDesbloqueo - Minutos restantes para desbloqueo automático (solo si temporal) - DEPRECATED: Ya no se usa
 * @property {(string|null)} fechaDesbloqueo - Fecha ISO de desbloqueo automático (solo si temporal) - DEPRECATED: Ya no se usa
 * 
 * @property {string} [jwtToken] - Token JWT (solo en login exitoso)
 * @property {string} [codigoUsuario] - ID del usuario (solo en login exitoso)
 * @property {string} [nombreUsuario] - Nombre del usuario (solo en login exitoso)
 * @property {string} [apellidoPaternoUsuario] - Apellido paterno (solo en login exitoso)
 * @property {string} [apellidoMaternoUsuario] - Apellido materno (solo en login exitoso)
 * @property {string} [correo] - Email del usuario (solo en login exitoso)
 * @property {string} [celular] - Celular del usuario (solo en login exitoso)
 * @property {number} [exp] - Timestamp de expiración del token (solo en login exitoso)
 * @property {boolean} [claveVencida] - Indica si la contraseña está vencida
 * @property {boolean} [claveProximaVencer] - Indica si la contraseña está próxima a vencer
 * @property {number} [diasRestantesContraseña] - Días restantes para vencimiento de contraseña
 * @property {number} [actualizaClave] - Flag de actualización de contraseña
 * @property {Object} [funcionalidades] - Funcionalidades del usuario
 */

/**
 * Valida si la respuesta corresponde a un bloqueo temporal
 * @param {LoginResponse} response - Respuesta del login
 * @returns {boolean}
 */
export function isTemporaryLock (response) {
  if (!response) return false
  return response.bloqueado === true && response.tipoBloqueo === 'TEMPORAL'
}

/**
 * Valida si la respuesta corresponde a un bloqueo permanente
 * @param {LoginResponse} response - Respuesta del login
 * @returns {boolean}
 */
export function isPermanentLock (response) {
  if (!response) return false
  return response.bloqueado === true && response.tipoBloqueo === 'PERMANENTE'
}

/**
 * Valida si hay intentos restantes
 * @param {LoginResponse} response - Respuesta del login
 * @returns {boolean}
 */
export function hasRemainingAttempts (response) {
  if (!response) return false
  return response.intentosRestantes !== null && response.intentosRestantes > 0
}

/**
 * Valida si la respuesta corresponde a un bloqueo por intentos fallidos (nuevo tipo)
 * @param {LoginResponse} response - Respuesta del login
 * @returns {boolean}
 */
export function isAttemptsLock (response) {
  if (!response) return false
  return response.bloqueado === true && response.tipoBloqueo === 'BLOQUEO_INTENTOS'
}

/**
 * Obtiene el mensaje apropiado según el estado de la respuesta
 * @param {LoginResponse} response - Respuesta del login
 * @returns {Object} Objeto con título y mensaje
 */
export function getLoginErrorMessage (response) {
  // Manejo de null/undefined
  if (!response) {
    return {
      title: '¡Lo sentimos!',
      message: 'Tu número de documento o contraseña es incorrecta. Por favor, inténtalo nuevamente.'
    }
  }

  // Bloqueo permanente
  if (isPermanentLock(response)) {
    return {
      title: '¡Lo sentimos!',
      message: response.mensajeRespuesta || 'Por tu seguridad, tu acceso ha sido bloqueado. Para continuar, comunícate con nuestros canales de atención al cliente.'
    }
  }

  // Bloqueo temporal
  if (isTemporaryLock(response)) {
    return {
      title: '¡Lo sentimos!',
      message: response.mensajeRespuesta || 'Por tu seguridad, tu acceso ha sido bloqueado temporalmente tras varios intentos fallidos. Podrás intentarlo nuevamente en unos minutos.'
    }
  }

  // Intentos restantes
  if (hasRemainingAttempts(response)) {
    return {
      title: 'Atención',
      message: response.mensajeRespuesta
    }
  }

  // Error genérico
  return {
    title: '¡Lo sentimos!',
    message: response.mensajeRespuesta || 'Tu número de documento o contraseña es incorrecta. Por favor, inténtalo nuevamente.'
  }
}




// WEBPACK FOOTER //
// ./src/interfaces/loginResponse.js